import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  SafeAreaView,
  Image,
  ImageBackground,
  Pressable,
} from 'react-native';

// Import assets for web compatibility
import mountainSplash from '../../assets/mountainSplash.png';
import meanderLogoWhite from '../../assets/MeanderEV-Logo-Final-white.png';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { clearSession } from '../store/actions/user-session';
import { UserSessionAttributes } from '../models/user-session-model';
import LoadingButton from '../components/LoadingButton';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import {
  resetPassword,
  checkOAuthStatus,
  isOAuthBlockedResponse,
  parseOAuthBlockedPayload,
} from '../services/password-reset-service';
import AsyncStorage from '@react-native-async-storage/async-storage';

function getTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
}

const ResetPasswordPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotModalVisible, setForgotModalVisible] = useState(false);
  const [isOAuthUser, setIsOAuthUser] = useState(false);
  const [oauthMessage, setOauthMessage] = useState('');
  const token = getTokenFromUrl();

  // Get user session from Redux store
  const userSession: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );
  const dispatch: AppDispatch = useDispatch();

  // Check if user is logged in and log them out when entering reset-password screen
  useEffect(() => {
    const handleLoggedInUser = async () => {
      console.log('Reset password page loaded, clearing session data...');

      // Always clear AsyncStorage to ensure clean state for password reset
      await AsyncStorage.clear();

      // If user session exists in Redux, also clear it from the server
      if (userSession && Array.isArray(userSession.role) && !userSession.role.includes('guest')) {
        console.log('User session found, logging out from server...');
        await dispatch(clearSession(userSession));
      }
    };

    handleLoggedInUser();
  }, [userSession, dispatch]);

  // Block OAuth (Google/Apple) accounts from the reset form using backend status
  useEffect(() => {
    const checkOAuthUser = async () => {
      if (!token) {
        return;
      }
      try {
        const response = await checkOAuthStatus(token);
        const oauthBlocked = isOAuthBlockedResponse(
          response.status,
          response.payload
        );
        const oauthFromPayload = parseOAuthBlockedPayload(response.payload);

        if (oauthBlocked || oauthFromPayload) {
          const message =
            oauthBlocked?.message ||
            oauthFromPayload?.message ||
            response.payload?.message ||
            '';
          setIsOAuthUser(true);
          setOauthMessage(message);
          setError(message);
        }
      } catch (error) {
        console.log(
          'Could not check OAuth status, proceeding with normal flow'
        );
      }
    };

    checkOAuthUser();
  }, [token]);

  const validate = () => {
    if (!newPassword || !confirmPassword) {
      setError('Please fill in all fields.');
      return false;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }
    setError('');
    return true;
  };

  const handleSubmit = async () => {
    if (isOAuthUser) {
      setError(
        oauthMessage ||
          'This account uses Google or Apple Sign-In. Please sign in with that provider instead.'
      );
      return;
    }

    if (!validate()) return;
    if (!token) {
      setError('Reset token is missing or invalid.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const resp = await resetPassword(token, newPassword);
      const oauthBlocked = isOAuthBlockedResponse(resp.status, resp.payload);
      if (resp.status === 200) {
        setSuccess(
          resp.payload?.message || 'Password has been reset successfully.'
        );
      } else if (oauthBlocked) {
        setIsOAuthUser(true);
        setOauthMessage(oauthBlocked.message);
        setError(oauthBlocked.message);
      } else {
        setError(resp.payload?.message || 'Reset failed. Please try again.');
      }
    } catch (e) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToMeanderEV = () => {
    if (typeof window !== 'undefined') {
      window.location.href =
        process.env.REACT_NATIVE_API_SERVER || 'https://app.meanderev.com';
    }
  };

  const handleForgotPasswordSuccess = (message: string) => {
    setSuccess('Password reset email sent! Please check your inbox.');
    setForgotModalVisible(false);
  };

  const handleForgotPasswordError = (message: string) => {
    // The modal will handle displaying the error internally
    console.log('Password reset error:', message);
  };

  const isTokenError =
    error &&
    (error.includes('token') ||
      error.includes('expired') ||
      error.includes('invalid') ||
      !token);

  const isSuccess = success && success.includes('successfully');

  return (
    <SafeAreaView style={styles.safeArea}>
      <ImageBackground
        source={mountainSplash}
        style={styles.background}
        resizeMode="cover"
      >
        <View style={styles.logoContainer}>
          <Image
            source={meanderLogoWhite}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>
        <View style={styles.container}>
          <Text style={styles.header}>Reset your MeanderEV password</Text>
          <Text style={styles.subtext}>Enter your new password below.</Text>

          {isOAuthUser && (
            <View style={styles.oAuthWarning}>
              <Text style={styles.oAuthWarningText}>
                Sign in with your linked account
              </Text>
              <Text style={styles.oAuthWarningSubtext}>
                {oauthMessage ||
                  'This account uses Google or Apple Sign-In. Password reset is not available. Please sign in with Google or Apple instead.'}
              </Text>
            </View>
          )}

          <TextInput
            placeholder="New password"
            placeholderTextColor="#8D8E90"
            style={[styles.input, isOAuthUser && styles.inputDisabled]}
            secureTextEntry
            value={newPassword}
            onChangeText={setNewPassword}
            editable={!isOAuthUser}
          />
          <TextInput
            placeholder="Confirm new password"
            placeholderTextColor="#8D8E90"
            style={[styles.input, isOAuthUser && styles.inputDisabled]}
            secureTextEntry
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            editable={!isOAuthUser}
          />
          {error ? <Text style={styles.error}>{error}</Text> : null}
          {isTokenError && (
            <Pressable
              onPress={() => setForgotModalVisible(true)}
              style={styles.requestTokenLink}
            >
              <Text style={styles.requestTokenText}>Request another token</Text>
            </Pressable>
          )}
          {success ? <Text style={styles.success}>{success}</Text> : null}
          <LoadingButton
            onPress={isSuccess ? handleGoToMeanderEV : handleSubmit}
            disabled={loading || isOAuthUser}
            style={{ marginTop: 16 }}
          >
            {loading
              ? 'Resetting...'
              : isSuccess
              ? 'Go to MeanderEV'
              : 'Reset Password'}
          </LoadingButton>
        </View>
      </ImageBackground>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        visible={forgotModalVisible}
        onClose={() => setForgotModalVisible(false)}
        onSuccess={handleForgotPasswordSuccess}
        onError={handleForgotPasswordError}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#1A1D21',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    backgroundColor: '#23262B',
    borderRadius: 16,
    padding: 32,
    width: 350,
    maxWidth: '95%',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
  },
  header: {
    color: 'white',
    fontSize: 22,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtext: {
    color: '#8D8E90',
    fontSize: 15,
    marginBottom: 18,
    textAlign: 'center',
  },
  input: {
    backgroundColor: '#292D33',
    color: 'white',
    borderRadius: 25,
    padding: 12,
    marginBottom: 10,
    width: 260,
    fontSize: 16,
  },
  error: {
    color: '#f38787',
    fontSize: 14,
    marginBottom: 8,
    textAlign: 'center',
  },
  success: {
    color: '#51B868',
    fontSize: 15,
    marginBottom: 8,
    textAlign: 'center',
  },
  requestTokenLink: {
    marginBottom: 8,
  },
  requestTokenText: {
    color: '#39BEB7',
    fontSize: 14,
    textDecorationLine: 'underline',
    textAlign: 'center',
  },
  background: {
    flex: 1,
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 18,
    marginTop: 48,
  },
  logo: {
    width: 300,
    height: 112,
    marginBottom: 8,
  },
  oAuthWarning: {
    backgroundColor: '#f38787',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  oAuthWarningText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  oAuthWarningSubtext: {
    color: 'white',
    fontSize: 14,
  },
  inputDisabled: {
    backgroundColor: '#444',
    opacity: 0.6,
  },
});

export default ResetPasswordPage;
